import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { FiEdit, FiEye, FiPlus, FiSearch, FiTrash2 } from "react-icons/fi";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import filter from "../../../src/assets/svg/Filter.svg";

// MUI Imports
import { IconButton, Tooltip } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

import CreateUserGroupModal from "./CreateUserGroupModal";
import GroupPreview from "./GroupPreview";
import "./UserGroup.css";

export const Domain = "https://estbn.com/EstbnNew/public/";
export const USER_GROUP = Domain + "api/portal/user-groups";
export const USER_DETAILS = Domain + "api/users";

const UserGroup = () => {
  const [groups, setGroups] = useState([]);
  // Loading state removed
  const [error, setError] = useState(null);

  // Pagination State for MUI DataGrid
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [totalEntries, setTotalEntries] = useState(0);

  const [showUserGroupModal, setShowUserGroupModal] = useState(false);
  const [groupToEdit, setGroupToEdit] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [detailedPreviewGroup, setDetailedPreviewGroup] = useState(null);
  const [detailedPreviewUsers, setDetailedPreviewUsers] = useState([]);
  const [
    detailedPreviewTargetingRuleString,
    setDetailedPreviewTargetingRuleString,
  ] = useState("No targeting rule defined.");
  const [detailedPreviewUsedInCampaigns, setDetailedPreviewUsedInCampaigns] =
    useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [inputSearchQuery, setInputSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  const filterPopupRef = useRef(null);
  const filterButtonRef = useRef(null);
  const { user } = useSelector((state) => state.auth);
  const token = user?.token;

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(inputSearchQuery);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 500);
    return () => clearTimeout(handler);
  }, [inputSearchQuery]);

  const fetchUserGroups = useCallback(async () => {
    // setLoading(true) removed
    setError(null);
    try {
      let url = `${USER_GROUP}?page=${paginationModel.page + 1}&per_page=${
        paginationModel.pageSize
      }`;

      if (debouncedSearchQuery) {
        url += `&search=${encodeURIComponent(debouncedSearchQuery)}`;
      }
      if (selectedType === "Dynamic") {
        url += `&is_dynamic=true`;
      } else if (selectedType === "Static") {
        url += `&is_dynamic=false`;
      }

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const { data, total } = response.data.data?.user_groups || {};

      if (!data) throw new Error("Invalid API response structure.");

      const formattedGroups = data.map((group) => ({
        ...group,
        id: group.id,
        type: group.is_dynamic ? "Dynamic" : "Static",
        usersCount: group.is_dynamic
          ? group.targeting_rule?.users_count || 0
          : group.user_ids?.length || 0,
        usedIn: group.campaigns?.length || 0,
        description: group.description || "No description provided",
      }));

      setGroups(formattedGroups);
      setTotalEntries(total || 0);
    } catch (err) {
      console.error("Failed to fetch user groups:", err);
      const errorMsg =
        err.response?.data?.message || "Failed to load user groups.";
      setError(errorMsg);
      toast.error(errorMsg);
      setGroups([]);
      setTotalEntries(0);
    }
    // finally block removed as setLoading(false) is no longer needed
  }, [paginationModel, selectedType, debouncedSearchQuery, token]);

  useEffect(() => {
    fetchUserGroups();
  }, [fetchUserGroups]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        filterPopupRef.current &&
        !filterPopupRef.current.contains(event.target) &&
        filterButtonRef.current &&
        !filterButtonRef.current.contains(event.target)
      ) {
        setShowFilterPopup(false);
      }
    };
    if (showFilterPopup)
      document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showFilterPopup]);

  const handleDelete = async (groupId) => {
    if (!window.confirm("Are you sure you want to delete this group?")) return;
    // setLoading(true) removed
    try {
      await axios.delete(`${USER_GROUP}/${groupId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("User group deleted successfully!");
      fetchUserGroups();
    } catch (err) {
      toast.error(
        err.response?.data?.message || "An error occurred while deleting."
      );
      // setLoading(false) removed
    }
  };

  const handleOpenCreateModal = () => {
    setGroupToEdit(null);
    setShowUserGroupModal(true);
  };
  const handleOpenEditModal = (group) => {
    setGroupToEdit(group);
    setShowUserGroupModal(true);
  };
  const handleCloseUserGroupModal = () => {
    setShowUserGroupModal(false);
    setGroupToEdit(null);
  };
  const handleGroupSaved = () => {
    fetchUserGroups();
    handleCloseUserGroupModal();
  };

  const handleOpenPreview = async (group) => {
    setPreviewLoading(true);
    setPreviewError(null);
    setDetailedPreviewGroup(null);
    setDetailedPreviewUsers([]);
    setDetailedPreviewTargetingRuleString("No targeting rule defined.");
    setDetailedPreviewUsedInCampaigns([]);
    setShowPreview(true);

    try {
      const groupResponse = await axios.get(`${USER_GROUP}/${group.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { user_group, campaign_count } = groupResponse.data.data;
      setDetailedPreviewGroup(user_group);

      let usersForPreview = [];
      if (user_group.is_dynamic) {
        setDetailedPreviewTargetingRuleString(
          JSON.stringify(user_group.targeting_rule?.criteria || {}, null, 2)
        );
      } else {
        setDetailedPreviewTargetingRuleString("Static group.");
        if (user_group.user_ids?.length > 0) {
          const allUsersResponse = await axios.get(USER_DETAILS, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const allUsers = allUsersResponse.data.data.users || [];
          usersForPreview = allUsers.filter((user) =>
            user_group.user_ids.includes(user.id)
          );
        }
      }
      setDetailedPreviewUsers(usersForPreview);
      setDetailedPreviewUsedInCampaigns(
        campaign_count > 0 ? [`${campaign_count} campaigns`] : []
      );
    } catch (err) {
      setPreviewError("Error loading details.");
      setDetailedPreviewGroup(group);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    setShowPreview(false);
    setDetailedPreviewGroup(null);
    setDetailedPreviewUsers([]);
    setPreviewError(null);
  };
  const handleTypeChange = (type) => {
    setSelectedType(type);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  // --- MUI Data Grid Columns ---
  const columns = [
    {
      field: "name",
      headerName: "Name",
      width: 230,
    },
    {
      field: "description",
      headerName: "Description",
      width: 250,
    },
    {
      field: "type",
      headerName: "Type",
      width: 150,
      renderCell: (params) => (
        <span
          className={`ugm-badge ${
            params.value ? params.value.toLowerCase() : ""
          }`}
        >
          {params.value}
        </span>
      ),
    },
    {
      field: "usersCount",
      headerName: "Users",
      width: 150,
      renderCell: (params) => params.value?.toLocaleString() || 0,
    },
    {
      field: "usedIn",
      headerName: "Used In",
      width: 250,
      renderCell: (params) =>
        `${params.value} campaign${params.value !== 1 ? "s" : ""}`,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <div className="ugm-actions-row">
          <Tooltip title="View">
            <IconButton
              size="small"
              onClick={() => handleOpenPreview(params.row)}
            >
              <FiEye className="ugm-icon-view" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleOpenEditModal(params.row)}
            >
              <FiEdit className="ugm-icon-edit" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => handleDelete(params.row.id)}
            >
              <FiTrash2 className="ugm-icon-delete" />
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="ugm-container">
      {/* Header Row: Title & Add Button */}
      <div className="ugm-header">
        <h2 className="ugm-title">User Groups</h2>
        <button className="ugm-btn-add" onClick={handleOpenCreateModal}>
          <FiPlus /> Add New Group
        </button>
      </div>

      {/* Filter Row: Search & Filter */}
      <div className="ugm-controls">
        <div className="ugm-entries-wrapper"></div>

        <div className="ugm-search-group">
          <div className="ugm-search-bar">
            <input
              type="text"
              placeholder="Search by name"
              value={inputSearchQuery}
              onChange={(e) => setInputSearchQuery(e.target.value)}
            />
            <FiSearch className="ugm-search-icon" />
          </div>

          <div className="ugm-filter-wrapper">
            <button
              className="ugm-btn-filter"
              onClick={() => setShowFilterPopup(!showFilterPopup)}
              ref={filterButtonRef}
            >
              <img src={filter} alt="Filter" />
            </button>
            {showFilterPopup && (
              <div className="ugm-filter-dropdown" ref={filterPopupRef}>
                <div className="ugm-filter-header">Choose Type</div>
                <label>
                  <input
                    type="radio"
                    name="gType"
                    checked={selectedType === null}
                    onChange={() => handleTypeChange(null)}
                  />{" "}
                  All
                </label>
                <label>
                  <input
                    type="radio"
                    name="gType"
                    checked={selectedType === "Dynamic"}
                    onChange={() => handleTypeChange("Dynamic")}
                  />{" "}
                  Dynamic
                </label>
                <label>
                  <input
                    type="radio"
                    name="gType"
                    checked={selectedType === "Static"}
                    onChange={() => handleTypeChange("Static")}
                  />{" "}
                  Static
                </label>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table Card */}
      <div className="ugm-table-card">
        <DataGrid
          rows={groups}
          columns={columns}
          // loading prop removed
          rowCount={totalEntries}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50]}
          disableRowSelectionOnClick
          rowHeight={60}
          sx={{
            border: "none",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#fff",
              color: "#374151",
              fontWeight: 700,
              borderBottom: "1px solid #f0f0f0",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #f0f0f0",
              color: "#1F2937",
              fontSize: "14px",
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "1px solid #f0f0f0",
            },
          }}
        />
      </div>

      <CreateUserGroupModal
        show={showUserGroupModal}
        onClose={handleCloseUserGroupModal}
        onGroupSaved={handleGroupSaved}
        groupToEdit={groupToEdit}
      />

      <GroupPreview
        show={showPreview}
        onClose={handleClosePreview}
        group={detailedPreviewGroup}
        detailedUsers={detailedPreviewUsers}
        targetingRule={detailedPreviewTargetingRuleString}
        usedInCampaigns={detailedPreviewUsedInCampaigns}
        loading={previewLoading}
        error={previewError}
      />
    </div>
  );
};

export default UserGroup;
